import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { TagFilter } from "@/components/tags/TagFilter";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MemoryModel, MediaModel, Contact, ContactModel, GroupModel } from "common247";
import {
  applyMemoryFilter,
  IMemoryFilterCriteria,
  IFilteredEntityResultSet,
} from "@/services/memory-filter-service";

export interface IMemoryFilterProps {
  /** Optional custom header title (defaults to "Filter Memories") */
  title?: string;
  /** Source pool of memories */
  allMemories: MemoryModel[];
  /** Source pool of media items */
  allMedia?: MediaModel[];
  /** Source pool of contacts */
  allContacts?: (Contact | ContactModel)[];
  /** Source pool of groups */
  allGroups?: GroupModel[];
  /** Callback triggered whenever filters change with the computed result set */
  onFilteredResultsChange: (results: IFilteredEntityResultSet) => void;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Initial filter criteria */
  initialCriteria?: IMemoryFilterCriteria;
}

const EMPTY_MEMORIES: MemoryModel[] = [];
const EMPTY_MEDIA: MediaModel[] = [];
const EMPTY_CONTACTS: (Contact | ContactModel)[] = [];
const EMPTY_GROUPS: GroupModel[] = [];

/**
 * Standardized Archival Heirloom Memory Filter component.
 * Filters across Memories, Media, Contacts, and Groups by date ranges, search query,
 * entity associations, media types, and ratings.
 */
export function MemoryFilter({
  title = "Filter Memories",
  allMemories = EMPTY_MEMORIES,
  allMedia = EMPTY_MEDIA,
  allContacts = EMPTY_CONTACTS,
  allGroups = EMPTY_GROUPS,
  onFilteredResultsChange,
  style,
  initialCriteria,
}: IMemoryFilterProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialCriteria?.searchQuery || "");
  const [dateFrom, setDateFrom] = useState<string>(
    initialCriteria?.dateFrom ? String(initialCriteria.dateFrom) : ""
  );
  const [dateTo, setDateTo] = useState<string>(
    initialCriteria?.dateTo ? String(initialCriteria.dateTo) : ""
  );
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(
    initialCriteria?.contactIds || []
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    initialCriteria?.groupIds || []
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    initialCriteria?.tagIds || []
  );
  const [selectedMediaType, setSelectedMediaType] = useState<string>("all");
  const [minRating, setMinRating] = useState<number>(initialCriteria?.minRating || 1);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");

  const onFilteredResultsChangeRef = useRef(onFilteredResultsChange);
  onFilteredResultsChangeRef.current = onFilteredResultsChange;
  const lastFingerprintRef = useRef<string>("");

  const criteria: IMemoryFilterCriteria = useMemo(
    () => ({
      searchQuery: searchQuery.trim() || undefined,
      dateFrom: dateFrom.trim() || null,
      dateTo: dateTo.trim() || null,
      contactIds: selectedContactIds.length > 0 ? selectedContactIds : undefined,
      groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      mediaTypes:
        selectedMediaType === "all"
          ? undefined
          : [selectedMediaType as "image" | "video" | "audio" | "document" | "pdf"],
      minRating: minRating > 1 ? minRating : undefined,
    }),
    [searchQuery, dateFrom, dateTo, selectedContactIds, selectedGroupIds, selectedTagIds, selectedMediaType, minRating]
  );

  const filteredResultSet = useMemo(
    () => applyMemoryFilter(allMemories, allMedia, allContacts, allGroups, criteria),
    [allMemories, allMedia, allContacts, allGroups, criteria]
  );

  useEffect(() => {
    const memFp = filteredResultSet.memories.map((m) => `${m.id}:${m.title || ""}:${m.favouriteRating ?? ""}:${m.updatedAt || ""}`).join(",");
    const medFp = filteredResultSet.media.map((m) => `${m.id}:${m.title || m.name || ""}:${m.favouriteRating ?? ""}:${m.updatedAt || ""}`).join(",");
    const conFp = filteredResultSet.contacts.map((c) => `${c.id}:${c.displayName || ""}:${c.email || ""}:${(c as any).updatedAt || ""}`).join(",");
    const grpFp = filteredResultSet.groups.map((g) => `${g.id}:${g.name || ""}:${g.updatedAt || ""}`).join(",");
    const fingerprint = `${memFp}|${medFp}|${conFp}|${grpFp}`;
    if (fingerprint !== lastFingerprintRef.current) {
      lastFingerprintRef.current = fingerprint;
      onFilteredResultsChangeRef.current?.(filteredResultSet);
    }
  }, [filteredResultSet]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (dateFrom.trim()) count++;
    if (dateTo.trim()) count++;
    if (selectedContactIds.length > 0) count += selectedContactIds.length;
    if (selectedGroupIds.length > 0) count += selectedGroupIds.length;
    if (selectedTagIds.length > 0) count += selectedTagIds.length;
    if (selectedMediaType !== "all") count++;
    if (minRating > 1) count++;
    return count;
  }, [searchQuery, dateFrom, dateTo, selectedContactIds, selectedGroupIds, selectedTagIds, selectedMediaType, minRating]);

  const handleResetFilters = (): void => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setSelectedContactIds([]);
    setSelectedGroupIds([]);
    setSelectedTagIds([]);
    setSelectedMediaType("all");
    setMinRating(1);
  };

  const toggleContact = (id: string): void => {
    setSelectedContactIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleGroup = (id: string): void => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: cardBg, borderColor }, style]}>
      {/* Summary Header & Toggle Bar */}
      <View style={styles.headerBar}>
        <TouchableOpacity
          style={styles.summaryRow}
          onPress={() => setIsExpanded(!isExpanded)}
          accessibilityRole="button"
          accessibilityLabel="Toggle memory filter options"
        >
          <View style={styles.titleWithBadge}>
            <Ionicons name="filter-outline" size={16} color={tintColor} />
            <ThemedText style={styles.headerTitle}>{title}</ThemedText>
            {activeFilterCount > 0 && (
              <View style={[styles.activeBadge, { backgroundColor: tintColor }]}>
                <ThemedText style={styles.activeBadgeText}>{activeFilterCount}</ThemedText>
              </View>
            )}
          </View>

          {/* Counts preview */}
          <View style={styles.countsRow}>
            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="memory" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>
                {filteredResultSet.totalCounts.memories}
              </ThemedText>
            </View>

            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="media" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>{filteredResultSet.totalCounts.media}</ThemedText>
            </View>

            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="contact" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>
                {filteredResultSet.totalCounts.contacts}
              </ThemedText>
            </View>

            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="group" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>
                {filteredResultSet.totalCounts.groups}
              </ThemedText>
            </View>
          </View>

          <Ionicons
            name={isExpanded ? "chevron-up" : "chevron-down"}
            size={18}
            color={tintColor}
            style={{ marginLeft: 6 }}
          />
        </TouchableOpacity>
      </View>

      {/* Expanded Filter Panel */}
      {isExpanded && (
        <View style={[styles.filterPanel, { borderTopColor: borderColor }]}>
          {/* Row 1: Search Query */}
          <View style={styles.filterRow}>
            <ThemedInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Search by title, narrative, or tags..."
              style={styles.inputField}
            />
          </View>

          {/* Row 2: Date From and Date To */}
          <View style={styles.dateRow}>
            <View style={styles.dateCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>From Date</ThemedText>
              <ThemedInput
                value={dateFrom}
                onChangeText={setDateFrom}
                placeholder="e.g. 2026, Jan 2026, 01-Jan-2026"
                style={styles.inputField}
              />
            </View>
            <View style={styles.dateCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>To Date</ThemedText>
              <ThemedInput
                value={dateTo}
                onChangeText={setDateTo}
                placeholder="e.g. 2026, Dec 2026, 31-Dec-2026"
                style={styles.inputField}
              />
            </View>
          </View>

          {/* Row 3: Media Type & Minimum Rating */}
          <View style={styles.optionsRow}>
            <View style={styles.optionCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>Media Type</ThemedText>
              <View style={styles.chipRow}>
                {["all", "image", "video", "audio", "document", "pdf"].map((t) => (
                  <TouchableOpacity
                    key={t}
                    style={[
                      styles.choiceChip,
                      { backgroundColor: badgeBg, borderColor },
                      selectedMediaType === t && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                    ]}
                    onPress={() => setSelectedMediaType(t)}
                  >
                    <ThemedText
                      style={[
                        styles.choiceChipText,
                        selectedMediaType === t && { color: "#1E3A8A", fontWeight: "700" },
                      ]}
                    >
                      {t.toUpperCase()}
                    </ThemedText>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.optionCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>Min Rating</ThemedText>
              <View style={styles.chipRow}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <TouchableOpacity
                    key={star}
                    style={[
                      styles.choiceChip,
                      { backgroundColor: badgeBg, borderColor },
                      minRating === star && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                    ]}
                    onPress={() => setMinRating(star)}
                  >
                    <ThemedText
                      style={[
                        styles.choiceChipText,
                        minRating === star && { color: "#1E3A8A", fontWeight: "700" },
                      ]}
                    >
                      {star === 1 ? "ALL" : `${star}★`}
                    </ThemedText>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Row 4: Contacts Filter */}
          {allContacts.length > 0 && (
            <View style={styles.entityFilterSection}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>
                Filter by Contacts ({selectedContactIds.length} selected)
              </ThemedText>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll}>
                {allContacts.map((c) => {
                  const isSelected = selectedContactIds.includes(c.id!);
                  return (
                    <TouchableOpacity
                      key={c.id}
                      style={[
                        styles.entityChip,
                        { backgroundColor: badgeBg, borderColor },
                        isSelected && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                      ]}
                      onPress={() => toggleContact(c.id!)}
                    >
                      <EntityIcon type="contact" size={12} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText
                        style={[
                          styles.entityChipText,
                          isSelected && { color: "#1E3A8A", fontWeight: "700" },
                        ]}
                        numberOfLines={1}
                      >
                        {c.displayName || c.email}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {/* Row 5: Groups Filter */}
          {allGroups.length > 0 && (
            <View style={styles.entityFilterSection}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>
                Filter by Groups ({selectedGroupIds.length} selected)
              </ThemedText>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll}>
                {allGroups.map((g) => {
                  const isSelected = selectedGroupIds.includes(g.id!);
                  return (
                    <TouchableOpacity
                      key={g.id}
                      style={[
                        styles.entityChip,
                        { backgroundColor: badgeBg, borderColor },
                        isSelected && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                      ]}
                      onPress={() => toggleGroup(g.id!)}
                    >
                      <EntityIcon type="group" size={12} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText
                        style={[
                          styles.entityChipText,
                          isSelected && { color: "#1E3A8A", fontWeight: "700" },
                        ]}
                        numberOfLines={1}
                      >
                        {g.name}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {/* Row 6: Tag Filter */}
          <View style={{ paddingHorizontal: 10, paddingVertical: 4 }}>
            <TagFilter
              selectedTagIds={selectedTagIds}
              onSelectedTagsChange={setSelectedTagIds}
            />
          </View>

          {/* Bottom Controls */}
          {activeFilterCount > 0 && (
            <View style={styles.footerRow}>
              <TouchableOpacity
                onPress={handleResetFilters}
                style={[styles.resetButton, { borderColor }]}
                accessibilityRole="button"
                accessibilityLabel="Clear all filters"
              >
                <Ionicons name="close-circle-outline" size={14} color="#EF4444" style={{ marginRight: 4 }} />
                <ThemedText style={styles.resetText}>Clear All Filters</ThemedText>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

export default MemoryFilter;

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 0,
    marginBottom: 10,
    width: "100%",
  },
  headerBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  titleWithBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: "700",
  },
  activeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 0,
  },
  activeBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "bold",
  },
  countsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  countChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderRadius: 0,
  },
  countText: {
    fontSize: 11,
    fontWeight: "600",
  },
  exportButtonsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: 8,
  },
  exportBtn: {
    height: 32,
    paddingHorizontal: 8,
    borderRadius: 0,
    borderWidth: 1,
  },
  exportBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  filterPanel: {
    borderTopWidth: 1,
    padding: 10,
    gap: 10,
  },
  filterRow: {
    width: "100%",
  },
  inputField: {
    borderRadius: 0,
    height: 36,
    fontSize: 12,
  },
  dateRow: {
    flexDirection: "row",
    gap: 10,
  },
  dateCol: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  optionsRow: {
    flexDirection: "row",
    gap: 12,
  },
  optionCol: {
    flex: 1,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  choiceChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 0,
  },
  choiceChipText: {
    fontSize: 10,
    fontWeight: "600",
  },
  entityFilterSection: {
    width: "100%",
  },
  chipsScroll: {
    flexDirection: "row",
    paddingVertical: 2,
  },
  entityChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 0,
    marginRight: 6,
  },
  entityChipText: {
    fontSize: 11,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 4,
  },
  resetButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 0,
  },
  resetText: {
    fontSize: 11,
    color: "#EF4444",
    fontWeight: "600",
  },
});
